import type { ConnectionStatus, ServerFrame } from "./types";

/**
 * WebSocket client for `/ws/v1.0/notifications/` (spec §2.4).
 *
 * Same shape as the chat and office realtime clients: JWT in the
 * `weco-jwt.v1` subprotocol, exponential backoff with jitter, and a 4401
 * close treated as "reconnect with a fresh token" before it is treated as
 * fatal. On top of that it speaks the notifications protocol: `hello` with the
 * last `seq` seen (the server replays what was missed), `ready` when the
 * replay is done, and a client `ping` every 8 s — nginx reaps sockets idle for
 * ~11 s, and the server's own 25 s ping is too slow to keep one alive.
 */

const SUBPROTOCOL = "weco-jwt.v1";
const CLIENT_PING_MS = 8_000;
/** No frame at all for this long ⇒ the connection is dead even if it looks open. */
const SILENCE_TIMEOUT_MS = 30_000;
const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
const JITTER = 0.25;
/** Consecutive 4401 closes tolerated (each after a token refresh) before giving up. */
const MAX_AUTH_FAILURES = 3;

export interface NotificationSocketOptions {
  url: string | (() => string);
  getToken: () => Promise<string | null>;
  /** Force a token refresh after a 4401; resolves false when the session is gone. */
  refreshToken?: () => Promise<boolean>;
  /** The highest `seq` already known; sent in `hello` so the server replays the rest. */
  getSince: () => number;
  onFrame: (frame: ServerFrame) => void;
  onStatus: (status: ConnectionStatus) => void;
  /** Called once auth keeps failing even with fresh tokens. */
  onAuthFailure?: () => void;
  WebSocketImpl?: typeof WebSocket;
}

export class NotificationSocket {
  private ws: WebSocket | null = null;
  private status: ConnectionStatus = "offline";
  private backoffMs = BACKOFF_MIN_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = true;
  private connecting = false;
  private authFailures = 0;
  /** True once a connection has been attempted — later attempts are "reconnecting". */
  private everTried = false;

  constructor(private opts: NotificationSocketOptions) {}

  getStatus(): ConnectionStatus {
    return this.status;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      detach(ws);
      try {
        ws.close(1000);
      } catch {
        /* already closing */
      }
    }
    this.everTried = false;
    this.backoffMs = BACKOFF_MIN_MS;
    this.authFailures = 0;
    this.setStatus("offline");
  }

  /** Skip the backoff wait (the browser came back online, the tab became visible). */
  reconnectNow(): void {
    if (this.stopped || this.ws || this.connecting) return;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    void this.connect();
  }

  private async connect(): Promise<void> {
    if (this.stopped || this.ws || this.connecting) return;
    this.connecting = true;
    this.setStatus(this.everTried ? "reconnecting" : "connecting");
    this.everTried = true;
    let token: string | null = null;
    try {
      token = await this.opts.getToken();
    } catch {
      token = null;
    }
    this.connecting = false;
    if (this.stopped) return;
    if (!token) {
      this.scheduleReconnect();
      return;
    }
    const url = typeof this.opts.url === "function" ? this.opts.url() : this.opts.url;
    const Impl = this.opts.WebSocketImpl ?? WebSocket;
    let socket: WebSocket;
    try {
      socket = new Impl(url, [SUBPROTOCOL, token]);
    } catch (err) {
      console.warn("[notifications-ws] open failed", err);
      this.scheduleReconnect();
      return;
    }
    this.ws = socket;
    socket.onopen = () => {
      if (this.ws !== socket) return;
      this.send({ type: "hello", since: Math.max(0, this.opts.getSince() || 0) });
      this.armSilenceTimer();
      this.pingTimer = setInterval(() => this.send({ type: "ping" }), CLIENT_PING_MS);
    };
    socket.onmessage = (ev: MessageEvent) => {
      if (this.ws !== socket) return;
      this.armSilenceTimer();
      let frame: ServerFrame;
      try {
        frame = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      if (!frame || typeof frame !== "object") return;
      if (frame.type === "ping") {
        this.send({ type: "pong" });
        return;
      }
      if (frame.type === "pong") return;
      if (frame.type === "ready") {
        this.backoffMs = BACKOFF_MIN_MS;
        this.authFailures = 0;
        this.setStatus("connected");
      }
      try {
        this.opts.onFrame(frame);
      } catch (err) {
        console.error("[notifications-ws] frame handler", err);
      }
    };
    socket.onerror = () => {
      /* the close event that follows drives the reconnect */
    };
    socket.onclose = (ev: CloseEvent) => {
      if (this.ws !== socket) return;
      this.ws = null;
      this.clearTimers();
      if (this.stopped) return;
      if (ev.code === 4401) {
        void this.handleAuthClose();
        return;
      }
      this.scheduleReconnect();
    };
  }

  private async handleAuthClose(): Promise<void> {
    this.authFailures += 1;
    if (this.authFailures > MAX_AUTH_FAILURES) {
      this.stopped = true;
      this.setStatus("offline");
      this.opts.onAuthFailure?.();
      return;
    }
    // The server closes 4401 shortly before a token expires as well as on a
    // genuine rejection — refresh first, then try again.
    try {
      await this.opts.refreshToken?.();
    } catch {
      /* the next connect() reads whatever token is stored */
    }
    this.scheduleReconnect(this.authFailures === 1 ? 0 : undefined);
  }

  private send(payload: unknown): void {
    if (this.ws && this.ws.readyState === 1 /* OPEN */) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  private setStatus(next: ConnectionStatus): void {
    if (next === this.status) return;
    this.status = next;
    this.opts.onStatus(next);
  }

  private armSilenceTimer(): void {
    if (this.silenceTimer !== null) clearTimeout(this.silenceTimer);
    this.silenceTimer = setTimeout(() => {
      console.warn("[notifications-ws] no frame in 30s — reconnecting");
      this.ws?.close();
    }, SILENCE_TIMEOUT_MS);
  }

  private clearTimers(): void {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    if (this.silenceTimer !== null) clearTimeout(this.silenceTimer);
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.pingTimer = null;
    this.silenceTimer = null;
    this.reconnectTimer = null;
  }

  private scheduleReconnect(delayOverride?: number): void {
    if (this.stopped || this.reconnectTimer !== null) return;
    this.setStatus("reconnecting");
    const jitter = (Math.random() * 2 - 1) * JITTER * this.backoffMs;
    const delay = delayOverride ?? Math.max(BACKOFF_MIN_MS, this.backoffMs + jitter);
    this.backoffMs = Math.min(BACKOFF_MAX_MS, this.backoffMs * 2);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }
}

function detach(ws: WebSocket): void {
  ws.onopen = null;
  ws.onmessage = null;
  ws.onerror = null;
  ws.onclose = null;
}

/**
 * Where the socket lives. Dev: the service's own port — the Vite proxy
 * forwards HTTP only, and matching "localhost" alone misses 127.0.0.1 (the
 * office lesson, see OfficeRealtimeProvider). Prod: the shell host serves
 * `/ws/`. `VITE_NOTIFICATIONS_WS_URL` overrides both.
 */
export function buildNotificationsWsUrl(loc: Pick<Location, "protocol" | "hostname" | "host"> = window.location): string {
  const envUrl = import.meta.env?.VITE_NOTIFICATIONS_WS_URL;
  if (typeof envUrl === "string" && envUrl.trim()) return envUrl.trim();
  const proto = loc.protocol === "https:" ? "wss" : "ws";
  const { hostname, host } = loc;
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]") {
    return `${proto}://${hostname}:8097/ws/v1.0/notifications/`;
  }
  return `${proto}://${host}/ws/v1.0/notifications/`;
}
